export type ShopType =
  | "kirana"
  | "restaurant"
  | "pharmacy"
  | "cloth"
  | "hardware"
  | "mobile"
  | "wholesale"
  | "other";

export interface ShopTypeMeta {
  id: ShopType;
  labelEn: string;
  labelHi: string;
  emoji: string;
}

export const SHOP_TYPES: ShopTypeMeta[] = [
  { id: "kirana", labelEn: "Kirana / Grocery", labelHi: "किराना", emoji: "🛒" },
  { id: "restaurant", labelEn: "Restaurant / Cafe", labelHi: "रेस्टोरेंट", emoji: "🍽️" },
  { id: "pharmacy", labelEn: "Pharmacy", labelHi: "मेडिकल", emoji: "💊" },
  { id: "cloth", labelEn: "Cloth & Apparel", labelHi: "कपड़ा", emoji: "👕" },
  { id: "hardware", labelEn: "Hardware", labelHi: "हार्डवेयर", emoji: "🔧" },
  { id: "mobile", labelEn: "Mobile / Electronics", labelHi: "मोबाइल", emoji: "📱" },
  { id: "wholesale", labelEn: "Wholesale", labelHi: "थोक", emoji: "📦" },
  { id: "other", labelEn: "Other", labelHi: "अन्य", emoji: "🏪" },
];

export interface SeedProduct {
  name: string;
  rate: number;
  unit: string;
  category: string;
  stock: number;
  hsn?: string;
  gstRate?: number;
  taxType?: "none" | "cgst-sgst" | "igst";
}

const SEEDS: Record<ShopType, SeedProduct[]> = {
  kirana: [
    { name: "Sugar 1kg", rate: 50, unit: "kg", category: "Groceries", stock: 20 },
    { name: "Atta 5kg", rate: 250, unit: "pack", category: "Groceries", stock: 10 },
    { name: "Tea 250g", rate: 130, unit: "pack", category: "Beverages", stock: 15 },
    { name: "Rice 5kg", rate: 320, unit: "pack", category: "Groceries", stock: 10 },
  ],
  restaurant: [
    { name: "Samosa", rate: 15, unit: "pcs", category: "Snacks", stock: 50 },
    { name: "Chai", rate: 10, unit: "cup", category: "Beverages", stock: 100 },
    { name: "Veg Thali", rate: 120, unit: "plate", category: "Meals", stock: 30 },
    { name: "Cold Drink", rate: 25, unit: "btl", category: "Beverages", stock: 40 },
  ],
  pharmacy: [
    { name: "Paracetamol 500mg", rate: 25, unit: "strip", category: "Medicine", stock: 30, hsn: "3004", gstRate: 12, taxType: "cgst-sgst" },
    { name: "Cough Syrup 100ml", rate: 90, unit: "btl", category: "Medicine", stock: 15, hsn: "3004", gstRate: 12, taxType: "cgst-sgst" },
    { name: "Bandage Roll", rate: 35, unit: "pcs", category: "First Aid", stock: 20, hsn: "3005", gstRate: 12, taxType: "cgst-sgst" },
    { name: "Hand Sanitizer 100ml", rate: 60, unit: "btl", category: "Hygiene", stock: 25, hsn: "3808", gstRate: 18, taxType: "cgst-sgst" },
  ],
  cloth: [
    { name: "T-Shirt (M)", rate: 350, unit: "pcs", category: "Apparel", stock: 25 },
    { name: "Saree (Cotton)", rate: 850, unit: "pcs", category: "Apparel", stock: 15 },
    { name: "Kurta Pyjama Set", rate: 1200, unit: "set", category: "Apparel", stock: 10 },
    { name: "Dupatta", rate: 200, unit: "pcs", category: "Apparel", stock: 30 },
  ],
  hardware: [
    { name: "Cement Bag 50kg", rate: 380, unit: "bag", category: "Construction", stock: 50, hsn: "2523", gstRate: 28, taxType: "cgst-sgst" },
    { name: "Paint 1L (White)", rate: 250, unit: "btl", category: "Paint", stock: 15, hsn: "3209", gstRate: 18, taxType: "cgst-sgst" },
    { name: "Hammer", rate: 180, unit: "pcs", category: "Tools", stock: 10, hsn: "8205", gstRate: 18, taxType: "cgst-sgst" },
    { name: "Screws (Pack of 100)", rate: 120, unit: "pack", category: "Fasteners", stock: 20, hsn: "7318", gstRate: 18, taxType: "cgst-sgst" },
  ],
  mobile: [
    { name: "Phone Charger", rate: 250, unit: "pcs", category: "Accessories", stock: 30, hsn: "8504", gstRate: 18, taxType: "cgst-sgst" },
    { name: "Earphones", rate: 350, unit: "pcs", category: "Accessories", stock: 25, hsn: "8518", gstRate: 18, taxType: "cgst-sgst" },
    { name: "Phone Cover", rate: 150, unit: "pcs", category: "Accessories", stock: 50, hsn: "3926", gstRate: 18, taxType: "cgst-sgst" },
    { name: "Screen Guard", rate: 100, unit: "pcs", category: "Accessories", stock: 40, hsn: "3919", gstRate: 18, taxType: "cgst-sgst" },
  ],
  wholesale: [
    { name: "Sugar (Sack 50kg)", rate: 2400, unit: "sack", category: "Bulk", stock: 20 },
    { name: "Atta (Sack 50kg)", rate: 2200, unit: "sack", category: "Bulk", stock: 15 },
    { name: "Oil Tin 15L", rate: 1800, unit: "tin", category: "Bulk", stock: 10 },
    { name: "Detergent (Carton)", rate: 1500, unit: "carton", category: "Household", stock: 20 },
  ],
  other: [
    { name: "Sample Item", rate: 100, unit: "pcs", category: "General", stock: 10 },
  ],
};

export function getSeedProductsForShopType(shopType: ShopType): SeedProduct[] {
  return SEEDS[shopType] ?? SEEDS.other;
}

export function shopTypeNeedsGst(shopType: ShopType): boolean {
  return ["pharmacy", "hardware", "mobile"].includes(shopType);
}

export function getDefaultGstRateForShopType(shopType: ShopType): number {
  switch (shopType) {
    case "pharmacy":
      return 12;
    case "restaurant":
      return 5;
    case "hardware":
    case "mobile":
      return 18;
    default:
      return 0;
  }
}

export function getShopTypeMeta(id: ShopType): ShopTypeMeta {
  return SHOP_TYPES.find((s) => s.id === id) ?? SHOP_TYPES[SHOP_TYPES.length - 1];
}
